// Per-user persistent lifecycle state for derived operational alerts.
// Backed by `operational_alert_states` (RLS-scoped to auth.uid()).
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/contexts/AuthContext';

export type AlertStatus = 'unread' | 'read' | 'acknowledged' | 'snoozed' | 'resolved' | 'dismissed';

export interface AlertStateRow {
  id: string;
  user_id: string;
  agency_id: string | null;
  alert_key: string;
  alert_kind: string | null;
  brand_id: string | null;
  status: AlertStatus;
  snoozed_until: string | null;
  acknowledged_at: string | null;
  resolved_at: string | null;
  dismissed_at: string | null;
  note: string | null;
  created_at: string;
  updated_at: string;
}

export function useAlertStates() {
  const { user } = useAuth();
  return useQuery({
    queryKey: ['alert-states', user?.id],
    enabled: !!user?.id,
    staleTime: 30_000,
    queryFn: async (): Promise<Record<string, AlertStateRow>> => {
      const { data, error } = await (supabase as any)
        .from('operational_alert_states')
        .select('*')
        .eq('user_id', user!.id);
      if (error) throw error;
      const map: Record<string, AlertStateRow> = {};
      (data ?? []).forEach((r: AlertStateRow) => { map[r.alert_key] = r; });
      return map;
    },
  });
}

interface UpsertArgs {
  alertKey: string;
  alertKind?: string | null;
  brandId?: string | null;
  agencyId?: string | null;
  status: AlertStatus;
  snoozedUntil?: string | null;
}

export function useUpdateAlertState() {
  const qc = useQueryClient();
  const { user } = useAuth();
  return useMutation({
    mutationFn: async (args: UpsertArgs) => {
      if (!user?.id) throw new Error('Not authenticated');
      const now = new Date().toISOString();
      const payload: any = {
        user_id: user.id,
        alert_key: args.alertKey,
        alert_kind: args.alertKind ?? null,
        brand_id: args.brandId ?? null,
        agency_id: args.agencyId ?? null,
        status: args.status,
        snoozed_until: args.snoozedUntil ?? null,
        updated_at: now,
      };
      if (args.status === 'acknowledged') payload.acknowledged_at = now;
      if (args.status === 'resolved') payload.resolved_at = now;
      if (args.status === 'dismissed') payload.dismissed_at = now;
      if (args.status !== 'snoozed') payload.snoozed_until = null;

      const { error } = await (supabase as any)
        .from('operational_alert_states')
        .upsert(payload, { onConflict: 'user_id,alert_key' });
      if (error) throw error;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['alert-states'] });
    },
  });
}

export function isStateActive(state: AlertStateRow | undefined): boolean {
  if (!state) return true;
  if (state.status === 'resolved' || state.status === 'dismissed') return false;
  if (state.status === 'snoozed' && state.snoozed_until && new Date(state.snoozed_until) > new Date()) {
    return false;
  }
  return true;
}

export function isUnread(state: AlertStateRow | undefined): boolean {
  if (!state) return true;
  return state.status === 'unread';
}

export const SNOOZE_OPTIONS: { label: string; hours: number }[] = [
  { label: '24 hours', hours: 24 },
  { label: '3 days', hours: 72 },
  { label: '7 days', hours: 168 },
];

export function snoozeIso(hours: number) {
  return new Date(Date.now() + hours * 3600 * 1000).toISOString();
}
